import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation } from "react-router";

import { canAdminister } from "@/features/auth/roles";
import { getUnreadCount } from "@/lib/api/endpoints";
import { keys } from "@/lib/api/keys";
import type { NavigationSection, SessionUser } from "@/lib/api/types";
import { cn } from "@/lib/utils";

import { buildNav } from "./sidebar/navItems";
import { SidebarGroup } from "./sidebar/SidebarGroup";
import { SidebarHeader } from "./sidebar/SidebarHeader";
import { SidebarUserCard } from "./sidebar/SidebarUserCard";

/**
 * Cuántas conversaciones tienen algo que esta persona todavía no vio.
 *
 * Se refresca cada minuto: más seguido es una consulta por persona por minuto
 * para un número que nadie mira en tiempo real; menos, y deja de ser un aviso.
 */
function useUnread(): number {
  const { data } = useQuery({
    queryKey: keys.inbox.unread(),
    queryFn: getUnreadCount,
    refetchInterval: 60_000,
    // Un contador que falla no puede romper la navegación entera.
    retry: false,
  });
  return data?.count ?? 0;
}

interface SidebarProps {
  sections: NavigationSection[];
  user: SessionUser;
  /** Móvil: el menú está desplegado sobre el contenido. */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function Sidebar({ sections, user, mobileOpen = false, onMobileClose }: SidebarProps) {
  const unread = useUnread();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  // Desplegado en móvil siempre va completo: el rail de íconos es cosa de escritorio.
  const compact = collapsed && !mobileOpen;
  const groups = buildNav(
    sections.flatMap((section) => section.modules),
    canAdminister(user.role),
  );

  return (
    <aside
      className={cn(
        "bg-rail text-rail-foreground border-rail-border/60 flex h-full flex-col border-r",
        "fixed inset-y-0 left-0 z-50 w-[252px] transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0",
        mobileOpen ? "translate-x-0" : "-translate-x-full",
        compact && "lg:w-16",
      )}
    >
      <SidebarHeader
        tenantName={user.tenant_name}
        compact={compact}
        onToggleCompact={() => setCollapsed((value) => !value)}
        onClose={onMobileClose}
      />

      <nav
        aria-label="Navegación principal"
        className="flex-1 overflow-x-hidden overflow-y-auto px-2.5 pt-2 pb-3"
      >
        {groups.map((group) => (
          <SidebarGroup
            key={group.label}
            group={group}
            pathname={pathname}
            unread={unread}
            compact={compact}
            onNavigate={onMobileClose}
          />
        ))}
      </nav>

      <SidebarUserCard user={user} compact={compact} />
    </aside>
  );
}
